import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { LocalVideoTrack, Room } from "twilio-video";

import "../../style/style.scss";
import { showToaster } from "../Toaster.tsx";
import Participants from "../participants/Participants.tsx";
import { clearQuotes, setSessionStatus } from "../../../action/share_screen/screenShare.action.js";
import { connectHostToRoom, copyToClipboard, createRoom, fetchIPAddress, getAuthToken, getUTCTimeFormatted, getIdentity, getBrowserName } from "../../utils/utils.ts";
import { const_var } from "../../../redux/reducers/reducer.js";
import { shortenUrl } from "../../../redux/reducers/utils.js";

type EndSource = "beforeunload" | "click";

type States = {
  isShowCopiedStatus: boolean;
  isControlPanelVisible: boolean;
  isEndSessionConfirmationDialogVisible: boolean;
  isExiting: boolean;
  room: Room | null;
  videoTrack: LocalVideoTrack | null;
  loading: {
    type: string;
    subHeading: string;
    button?: {
      label: string;
      onClick: () => void;
    };
  };
};

type Props = {
  state: States;
  setState: (key: string, value: any) => void;
  navBoxClassName: string;
  className: string;
};

let inactivityTimer: NodeJS.Timeout;

const INACTIVITY_THRESHOLD = 60000 * 10; // 10 minutes



const ScreenShareControlPanel = ({  state, setState, navBoxClassName, className }: Props) => {
  const dispatch = useDispatch();

  const createdQuotes = useSelector((state) => state?.screenShareReducer?.createdQuotes ?? []);

  const saveIpAddress = async () => {
    const ipAddress = await fetchIPAddress();
    sessionStorage.setItem("ipAddress", ipAddress);
  };

  useEffect(() => {
    saveIpAddress();
  }, []);

  const { room, loading, videoTrack, isExiting, isEndSessionConfirmationDialogVisible, isShowCopiedStatus }: States = state;

  const [sharableLink, setSharableLink] = useState<any>("");

  const handleEmailClick = () => {
    sessionStorage.setItem("E", "0");
    const email = "";
    const subject = "Link to join the meeting";
    const body = `Please click on this link to join the meeting: ${sharableLink}`;
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, "_self");
    sessionStorage.removeItem("E");
  };

  //  pause/stop video stream (DOES NOT MEAN ENDING SESSION)
  const stopVideoTrack = ({ vidTrack, shouldRemoveOverlay = true, _room }: { vidTrack?: LocalVideoTrack; shouldRemoveOverlay?: boolean; _room?: Room }) => {
    let vT = videoTrack || vidTrack;
    let rooom = room || _room;
    // TO STOP CAPTURING STREAM
    vidTrack?.stop && vidTrack?.stop();
    videoTrack?.stop && videoTrack?.stop();
    // TO INFORM TWILIO THAT WE ARE STOPPING THE TREAM
    if (rooom && vT) {
      rooom.localParticipant.unpublishTrack(vT);
    }
    setState("videoTrack", null);
    setTimeout(() => {
      shouldRemoveOverlay && setState("isControlPanelVisible", false);
      shouldRemoveOverlay && removeOverlay();
    }, 1000);
  };

  const shareVideoTrack = async (room: Room, videoStream: MediaStream) => {
    const { mediaStreamTrack, localVideoTrack } = await getVideoTrack(videoStream);

    room?.localParticipant.publishTrack(localVideoTrack);

    mediaStreamTrack.onended = () => {
      stopVideoTrack({ vidTrack: localVideoTrack, _room: room });
    };

    return localVideoTrack;
  };

  const toggleScreenShare = async () => {
    if (videoTrack) {
      stopVideoTrack({ vidTrack: videoTrack });
    } else {
      const videoStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          // @ts-ignore
          preferCurrentTab: true,
          mediaSource: "tab",
          logicalSurface: true,
          surfaceSwitching: "exclude",
          displaySurface: "browser",
          width: 3840,
          height: 2160,
          frameRate: 120
        },
        mediaSource: "tab",
        logicalSurface: true,
        surfaceSwitching: "exclude",
        preferCurrentTab: true // to show current tab only
      });
      room && (await shareVideoTrack(room, videoStream));
      setTimeout(() => {
        setState("isControlPanelVisible", false);
        removeOverlay();
      }, 1000);
    }
  };

  const monitorScreenSharing = (videoTrack: LocalVideoTrack) => {
    if (!videoTrack) return;

    const checkActivity = () => {
      if (videoTrack.mediaStreamTrack.readyState === "ended") {
        clearTimeout(inactivityTimer);
        return;
      }

      inactivityTimer = setTimeout(async () => {
        await endSession("click", "Session has been ended due to inactivity", videoTrack);
      }, INACTIVITY_THRESHOLD);
    };

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      checkActivity();
    };

    document.addEventListener("mousemove", resetTimer);
    document.addEventListener("keydown", resetTimer);
    document.addEventListener("click", resetTimer);

    checkActivity();
  };

  // to fetch sharable media, create localTrack and return
  const getVideoTrack = async (videoStream: MediaStream) => {
    const screen = document.getElementsByTagName("body")[0];
    const overlay = document.createElement("div");
    overlay.classList.add("fade", "modal-backdrop", "show", "ract-modal-pre-parent");
    screen.appendChild(overlay);
    try {
      const mediaStreamTrack = videoStream.getTracks()[0];

      const localVideoTrack = new LocalVideoTrack(mediaStreamTrack);

      monitorScreenSharing(localVideoTrack);

      setState("videoTrack", localVideoTrack);

      screen.removeChild(overlay);
      return {
        mediaStreamTrack,
        localVideoTrack
      };
    } catch (err) {
      console.log("error", err);
      screen.removeChild(overlay);
      throw new Error("Error in getting video/screen");
    }
  };

  const destroyRoom = async (source: EndSource) => {
    const roomName = sessionStorage.getItem("ROOMNAME");
    sessionStorage.removeItem("ROOMNAME");
    if (roomName) {
      if (source === "beforeunload") {
        navigator.sendBeacon(`${process.env.REACT_APP_SS_BASE_URL}/destroy-room?roomId=${roomName}`);
      } else {
        await axios.post(`${process.env.REACT_APP_SS_BASE_URL}/destroy-room?roomId=${roomName}`);
      }
    }
  };

  const handleShareButton = async (e) => {
    // start sharing
    if ((state.loading.type === "error" || state.loading.type === "none") && state.room?.state !== "connected") {
      try {
        sessionStorage.setItem("startTime", getUTCTimeFormatted());

        setState("loading", {
          type: "loading",
          subHeading: "We are starting the screen-sharing session"
        });

        const roomId = "room-" + Math.random().toString(36).substr(2, 9);

        const identifiableToken = getIdentity("sensei-host", true);

        await createRoom(roomId);
        let _room: Room | null = null;

        const browserName = getBrowserName();
        sessionStorage.setItem("browserName", browserName);

        const isSafari = browserName === "Apple Safari";
        if (!isSafari) {
          const authToken = await getAuthToken(identifiableToken, roomId);
          _room = await connectHostToRoom(authToken, roomId);
        }

        const videoStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            // @ts-ignore
            preferCurrentTab: true,
            mediaSource: "tab",
            logicalSurface: true,
            surfaceSwitching: "exclude",
            displaySurface: "browser",
            width: 3840,
            height: 2160,
            frameRate: 120
          },
          mediaSource: "tab",
          logicalSurface: true,

          surfaceSwitching: "exclude",
          preferCurrentTab: true // to show current tab only
        });

        if (isSafari) {
          const authToken = await getAuthToken(identifiableToken, roomId);
          _room = await connectHostToRoom(authToken, roomId);
        }

        _room && startSession(_room, videoStream);
        return;
      } catch (error) {
        sessionStorage.removeItem("startTime");
        sessionStorage.removeItem("ROOMNAME");

        setState("loading", { type: "none" });

        if (error.code === 0) {
          showToaster("Permission denied. Please try again & allow screen sharing to continue");
          return;
        }
        showToaster("Something went wrong. Please try again later");
        await destroyRoom("click");
      }
    }

    // toggle dialog box visibility
    if (state.room?.state === "connected") {
      if (!state.isControlPanelVisible) {
        addOverlay({ onClick: () => setState("isControlPanelVisible", false) });
      } else {
        removeOverlay();
      }
      if (!state.isControlPanelVisible) {
        setState("isShowCopiedStatus", false);
      }
      setState("isControlPanelVisible", !state.isControlPanelVisible);
      setState("isEndSessionConfirmationDialogVisible", false);
    }
  };

  const startSession = async (_room: Room, videoStream: MediaStream) => {
    try {
      const vidTrack = await shareVideoTrack(_room, videoStream);

      _room.on("disconnected", async (_, error) => {
        if (error.code === 53001) {
          await endSession("click", "Disconnected due to network issue. Please try again later.", vidTrack, true);
        }
        if (error.code === 53216) {
          await endSession("click", "Session time limit exceeded. Please start a new session to continue.", vidTrack);
        }
      });

      _room.on("reconnecting", () => {
        console.warn("Reconnecting to Twilio...");
        showToaster("Reconnecting due to network issue...");
      });

      _room.on("reconnected", () => {
        console.log("Successfully reconnected!");
        showToaster("Reconnected successfully!");
      });

      setState("room", _room);



      let url = window.location.href, link:string;
      if(url.includes("pre.senseicrm") || url.includes("crm.senseicrm")) {
        link = `${window.location.origin}/join/${_room.name}`
      } else {
        link = `${window.location.origin}/#/join/${_room.name}`
      }
      // Generate shareable link
      const res = await shortenUrl(link);
      if (res) {
        setSharableLink(res);
      } else {
        setSharableLink(link)
      }

      setState("loading", { type: "none", subHeading: "" });

      // Set status true
      setState("isControlPanelVisible", true);
      setState("isShowCopiedStatus", false);
      dispatch(setSessionStatus(true));

      // adding overlay and passing a callback function to execute on removal of overlay
      addOverlay({ onClick: () => setState("isControlPanelVisible", false) });
    } catch (error) {
      // We will NOT treat this as error and close everything
      if (error.message !== "Error in getting video/screen") {
        showToaster("Something went wrong. Please try again later");
      }
      setState("loading", { type: "none" });
      setState("isControlPanelVisible", false);
      setState("room", null);
      removeOverlay();
      setSharableLink("");
      await destroyRoom("click");
      console.log("Error sharing screen:", error);
    }
  };

  const endSession = async (source: EndSource = "click", toasterMessage = "This session has been ended", vidTrack: LocalVideoTrack | null = null, doNotDestroyRoom = false) => {
    try {
      setState("loading", { type: "none" });
      setState("isEndSessionConfirmationDialogVisible", false);
      setState("isExiting", true);

      if (!doNotDestroyRoom) {
        await destroyRoom(source);
      }

      source === "click" && setTimeout(() => showToaster(toasterMessage));

      if (!doNotDestroyRoom) {
        logEntry();
      }

      setState("isExiting", false);

      setState("isControlPanelVisible", false);
      dispatch(setSessionStatus(false));
      removeOverlay();

      setSharableLink("");
      const args = { shouldRemoveOverlay: false };
      if (vidTrack) {
        args["vidTrack"] = vidTrack;
      }
      stopVideoTrack(args);
      sessionStorage.removeItem("startTime");
      sessionStorage.removeItem("ROOMNAME");
    } catch (err) {
      console.error("Error in isExiting", err);
    }
  };

  const logEntry = () => {
    const startTime = sessionStorage.getItem("startTime");
    const ipAddress = sessionStorage.getItem("ipAddress");
    const browserName = sessionStorage.getItem("browserName");

    if (!startTime || !ipAddress || !browserName) return;

    if (const_var.crmSetting.is_module_name === "inventory") {
      return;
    }

    const endTime = getUTCTimeFormatted();

    let log_data: { module: string; module_id: string } | null = null;
    // CASE 1. Not created/updated the quote
    if (createdQuotes.length === 0) {
      // CASE 1.1 If user is editing the quote
      if (const_var.crmSetting.is_Edit) {
        log_data = {
          module: const_var.crmSetting.sub_module, // can be lead or customer
          module_id: const_var.crmSetting.sub_module_id // module id
        };
      }
      // CASE 1.2 If user is creating the quote
      else {
        // CASE 1.2.1 If user is creating the quote from lead/customer
        if (const_var.crmSetting.sub_module_id) {
          log_data = {
            module: const_var.crmSetting.sub_module,
            module_id: const_var.crmSetting.sub_module_id
          };
        }
        // CASE 1.2.2 If user is creating the quote from user
        else {
          log_data = {
            module: "user",
            module_id: const_var.crmSetting.login_user_id
          };
        }
      }
    }
    // CASE 2. Created/updated the quote
    else {
      const extractedInfo = extractModuleInfo(createdQuotes[0]?.sub_module_url);

      if (extractedInfo) {
        log_data = {
          module: extractedInfo?.module_name,
          module_id: extractedInfo?.module_id
        };
      }
    }

    if (!log_data) return;

    const data = {
      api_token: const_var.crmSetting.api_token,
      module: log_data.module,
      module_id: log_data.module_id,
      subject: "Screen share log",
      description: `shared screen from ${startTime} to ${endTime}. IP Address: ${ipAddress} and Browser: ${browserName}`
    };
    navigator.sendBeacon(
      const_var.crmSetting.api_post_url + "/api/v1/shared-screen/log" + `?api_token=${data.api_token}&module=${data.module}&module_id=${data.module_id}&subject=${data.subject}&description=${data.description}`
    );

    dispatch(clearQuotes());
  };

  function extractModuleInfo(url: string) {
    if (url == undefined) return null;
    const regex = /\/([a-zA-Z0-9_-]+)\/([a-zA-Z0-9_-]+)$/;
    const match = url.match(regex);
    if (match && match?.length === 3) {
      return {
        module_name: match[1].includes("lead") ? "lead" : "customer",
        module_id: match[2]
      };
    }
    return null;
  }

  // DESTROY ROOM AND EXIT before you leave this tab or browser
  useEffect(() => {
    window.onbeforeunload = () => {
      if (sessionStorage.getItem("E") !== "0") {
        endSession("beforeunload");
      }
    };
    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  const EndSessionDialog = () => {
    return (
      <div
        style={{
          opacity: isEndSessionConfirmationDialogVisible ? 1 : 0,
          visibility: isEndSessionConfirmationDialogVisible ? "visible" : "hidden",
          transition: "all 0.3s ease-in-out"
        }}
        className={`end-session-tooltip`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="alert-title">ALERT!!</div>
        <div className="alert-content">{`Are you sure you want to END this Session?`}</div>
        <div className="action-container">
          <button className="btn btn-danger" onClick={async () => await endSession()}>
            YES
          </button>
          <button className="btn btn-default" onClick={() => setState("isEndSessionConfirmationDialogVisible", false)}>
            NO
          </button>
        </div>
        <i className="icon-close" onClick={() => setState("isEndSessionConfirmationDialogVisible", false)}></i>
      </div>
    );
  };

  const Loading = () => {
    return (
      <>
        <i className="icon-ss-share-empty-box"></i>
        <span className="active saving-effect saving-effect-type-2">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </>
    );
  };

  const Sharing = () => {
    return (
      <div id="show_control_panel" className="show-sharing-container">
        {!state.videoTrack && <i className="icon-ss-paused"></i>}
        <p className={`sharing-text ${state.videoTrack ? "" : " paused "}`}>{state.videoTrack ? "Sharing" : "Paused"}</p>
        {state.videoTrack && <span className={state.isControlPanelVisible ? "icon icon-close" : "icon-arrow-right-ico"} />}
      </div>
    );
  };

  return (
    <div className={navBoxClassName}>
      <div className={`${className} screen-share-btn  ${state.room?.state === "connected" ? " showSharing " : ""} ${state.videoTrack ? "" : " paused"}`} data-tut="screen-share-data-tut" onClick={handleShareButton}>
        <div className="d-flex flex-column">
          {state.room?.state === "connected" ? (
            <Sharing />
          ) : state.loading.type === "loading" ? (
            <Loading />
          ) : (
            <>
              <i className="icon-ss-share-box"></i>
              <div className="share-screen-title-tooltip">Share Your Screen</div>
              <div className="new-feature">
                <i className="icon-ss-new-tag"></i>
              </div>
            </>
          )}
        </div>
        {!state.isControlPanelVisible && state.room?.state === "connected" && <div className={`sharing-pulse ${state.videoTrack ? "" : "warning"}`}></div>}
        <div
          style={{
            opacity: state.isControlPanelVisible ? 1 : 0,
            visibility: state.isControlPanelVisible ? "visible" : "hidden",
            transition: `all 0.3s ease-in-out`,
            display: "block",
            zIndex: 100
          }}
          className={`screen-share-modal ${state.loading.type}-container ${state.room?.state}`}
          onClick={(e) => e.stopPropagation()}
        >
          <>
            <div className={`share-screen-container`}>
              {loading?.type === "error" ? (
                <div className={`status-container`}>
                  {loading.type === "error" && (
                    <div className={`status-image-container`}>
                      <i className="icon-ss-error"></i>
                      <div className="circle1"></div>
                      <div className="circle2"></div>
                      <div className="circle3"></div>
                    </div>
                  )}
                  <h6 className="heading-text" style={{ color: loading.type === "error" ? "#D5121A" : "" }}>
                    {"Something went wrong!"}
                  </h6>
                  <span className="sub-heading-text">{loading.subHeading}</span>
                  {loading.button && (
                    <button
                      style={
                        loading.type === "error"
                          ? {
                              color: "#444444",
                              borderColor: "#CCCCCC",
                              backgroundColor: "transparent",
                              marginTop: "18px"
                            }
                          : {}
                      }
                      onClick={async (e) => loading?.button?.onClick && loading.button.onClick()}
                      className="btn btn-primary"
                    >
                      {loading.button.label}
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {!sharableLink ? null : (
                    <>
                      {/* LEFT SIDE */}
                      <div className="share-screen-left">
                        {/* SHARE LINK */}
                        <div className="share-link-container">
                          <div className="copy-link-title-container">
                            <p className="copy-link-title">Share this link via Email Or Copy Link from here</p>
                            <span className="icon-point-down-hand" />
                          </div>
                        </div>

                        {/* COPY LINK */}
                        <div className="copy-link-container">
                          <div className="share-link-content ">
                            {/* EMAIL */}
                            <div className="share-link-content-item ">
                              <div onClick={handleEmailClick} className="share-link-img-container">
                                <i className="icon-ss-email"></i>
                              </div>
                              <p className="text-secondary">Email</p>
                            </div>
                          </div>

                          {/* COPY BOX */}
                          <div className="copy-box-container">
                            <p className="copy-link-text">{sharableLink}</p>
                            {isShowCopiedStatus ? (
                              <div className="copied-status-container">
                                <span className="copied-status-text">COPIED</span>
                                <span className="icon-checkmark-ico"></span>
                              </div>
                            ) : (
                              <button
                                onClick={async () => {
                                  if (await copyToClipboard(sharableLink)) {
                                    setState("isShowCopiedStatus", true);
                                  }
                                  setTimeout(() => {
                                    setState("isControlPanelVisible", false);
                                    removeOverlay();
                                  }, 1000);
                                }}
                                className="clb copy-link-btn btn bg-transparent"
                              >
                                <i style={{fontSize : '16px', marginRight : 4}} className="icon-ss-copy"></i>
                                COPY LINK
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* RIGHT SIDE */}
                      <div className="share-screen-right">
                        {videoTrack ? (
                          <button className="stop-sharing btn btn-primary" onClick={toggleScreenShare}>
                            {"STOP SHARING"}
                          </button>
                        ) : (
                          <button className="resume-sharing btn" onClick={toggleScreenShare}>
                            {"RESUME..."}
                          </button>
                        )}

                        <button className="end-session btn btn-danger" onClick={() => setState("isEndSessionConfirmationDialogVisible", true)}>
                          {isExiting ? "ENDING..." : "END SESSION"}
                          <EndSessionDialog />
                        </button>
                        <Participants room={room} />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </>
        </div>
      </div>
    </div>
  );
};

export const addOverlay = ({ onClick: onRemove }) => {
  const screen = document.getElementsByTagName("header")[0];
  screen.style.zIndex = "150";

  const overlay = document.createElement("div");
  overlay.id = "screen-share-overlay";
  overlay.classList.add("screen-share-overlay");
  screen.appendChild(overlay);

  setTimeout(() => {
    overlay.classList.add("show");
  }, 100);

  overlay.addEventListener("click", () => {
    removeOverlay();
    onRemove && onRemove();
  });
};

export const removeOverlay = () => {
  const overlay = document.getElementById("screen-share-overlay");

  if (overlay) {
    // Remove show class to trigger fade-out effect
    overlay.classList.remove("show");

    // Wait for transition to finish before removing
    setTimeout(() => {
      const screen = document.getElementsByTagName("header")[0];
      screen.style.zIndex = "20";
      overlay.remove();
    }, 300);
  }
};

export default ScreenShareControlPanel;
